import { resetGems } from "@/components/c360/GemMark";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { GRADE, VIEW_TAB } from "@/lib/c360Profile";
import { tiles } from "@/lib/c360Tiles";
import { CYAN, bl, rWord, useBlur } from "@/lib/c360Mine";

const PICKS = ["strong", "good", "weak"];

function Pick({ value, kol, onPick, testid, small }) {
  return (
    <div className="flex gap-2" data-testid={testid}>
      {PICKS.map((g) => {
        const on = value === g;
        return (
          <div key={g} className="flex flex-col items-center gap-1">
            <button type="button" onClick={() => onPick(g)} data-testid={`${testid}-${g}`} data-on={on ? "1" : "0"}
              className={`${small ? "h-8 w-[72px] text-xs" : "h-10 w-[96px] text-sm"} rounded-md border transition-colors font-medium`}
              style={on ? { borderColor: CYAN, color: CYAN, background: "rgba(0,240,255,0.08)" } : { borderColor: "rgba(255,255,255,0.12)", color: GRADE[g].color }}>{GRADE[g].word}</button>
            <span className="mono text-[8px] tracking-[0.15em] text-zinc-500 h-2.5">{kol === g ? "KOL RADAR'S" : ""}</span>
          </div>
        );
      })}
    </div>
  );
}

const editable = (av, d) => tiles(av, d).filter((t) => t.k !== "bot" && !t.keeps && !t.makes);

export function MineEditor({ av, d, onClose, onSaved }) {
  const m = d.mine?.[av];
  const kol = d.av[av].r;
  const blur = useBlur();
  const [r, setR] = useState(m?.r || (kol === "unknown" ? null : kol));
  const [checks, setChecks] = useState(m?.checks || {});
  const [note, setNote] = useState(m?.note || "");
  const [busy, setBusy] = useState(false);
  const url = `/creator360/mine/${d.creator.id}/${av}`;
  const run = async (fn) => {
    setBusy(true);
    try { onSaved(av, (await fn()).data.mine); resetGems(); onClose(); } catch { toast.error("Your change could not be saved. Please try again."); } finally { setBusy(false); }
  };
  const save = () => {
    const changed = (r && r !== kol) || Object.keys(checks).length > 0;
    if (!changed && !m) { toast("Nothing to save: pick a verdict or a check different from KOL Radar's."); return; }
    run(() => api.put(url, { r: r === kol ? null : r, checks, note }));
  };
  const reset = () => run(() => api.delete(url));
  const ts = editable(av, d);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[640px] max-h-[90vh] overflow-y-auto border hairline p-6" style={{ background: "var(--surface-2, #111113)" }} data-testid="c360-editor">
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-cyan-400">Change the verdict</div>
        <DialogTitle className="text-xl text-white font-semibold" data-testid="c360-editor-title">{VIEW_TAB[av]} · <span className={bl(blur)}>{d.creator.name}</span></DialogTitle>
        <DialogDescription className="text-sm text-zinc-400" data-testid="c360-editor-line">KOL Radar says {rWord(kol)}. Your change is saved for you only and shows in cyan, with KOL Radar's verdict kept beside it.</DialogDescription>
        <div className="mt-4">
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Your verdict</div>
          <Pick value={r} kol={kol} onPick={setR} testid="c360-editor-verdict" />
        </div>
        {ts.length > 0 && (
          <div className="mt-5" data-testid="c360-editor-checks">
            <div className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">The checks · optional</div>
            <p className="text-xs text-zinc-500 mt-1 mb-2">Changing a check does not change the verdict by itself.</p>
            <div className="divide-y divide-white/5">
              {ts.map((t) => (
                <div key={t.k} className="flex flex-wrap items-center justify-between gap-3 py-2" data-testid={`c360-editor-check-${t.k}`}>
                  <div className="min-w-0">
                    <div className="text-sm text-zinc-200">{t.name} <span className="text-white font-semibold ml-1">{t.value}</span></div>
                    <div className="mono text-[10px] text-zinc-500">KOL Radar: {GRADE[t.grade]?.word || "Not judged"}</div>
                  </div>
                  <Pick small value={checks[t.k] || t.grade} kol={t.grade} testid={`c360-editor-check-${t.k}-pick`}
                    onPick={(g) => setChecks((c) => { const n = { ...c }; if (g === t.grade) delete n[t.k]; else n[t.k] = g; return n; })} />
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="mt-5">
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Your note</div>
          <textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 500))} maxLength={500} rows={3} data-testid="c360-editor-note"
            placeholder="Why are you changing it? For example: their Telegram posts drive sign-ups for us."
            className="w-full rounded-md border hairline bg-transparent p-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-cyan-400/60" />
          <div className="mono text-[10px] text-zinc-600 text-right">{note.length}/500</div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
          <button type="button" onClick={reset} disabled={busy || !m} data-testid="c360-editor-reset" className="text-xs text-zinc-400 hover:text-white underline underline-offset-4 disabled:opacity-40 disabled:no-underline">Use KOL Radar's verdict again</button>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} data-testid="c360-editor-cancel" className="h-9 px-4 rounded-md border hairline text-sm text-zinc-200 hover:border-white/30">Cancel</button>
            <button type="button" onClick={save} disabled={busy} data-testid="c360-editor-save" className="h-9 px-5 rounded-md text-sm font-semibold text-black bg-cyan-400 hover:bg-cyan-300 disabled:opacity-50">Save</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
